import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtMoney, fmtNumber } from '../lib/format';
import type { MarginLevel } from '../lib/types';
import { Button, Callout, Dialog, Field, Input, Ltr, useConfirm, useToast } from '../components/ui';
import { MarginBadge } from '../components/Margin';
import { ProductPicker } from '../components/ProductPicker';
import { IconPlus, IconTrash } from '../components/icons';

type Member = { productId: number; sku: string; name: string; basePrice: number | null; cost: number | null; margin: number | null; marginLevel: MarginLevel | null };
type Group = { id: number; name: string; status: 'proposed' | 'approved' | 'rejected'; note: string | null; members: Member[] };
type Groups = { target: number; warn: number; groups: Group[] };

/** Colour / size variants of one product shown once. */
function byProduct(members: Member[]) {
  const out = new Map<string, { first: Member; variants: number }>();
  for (const m of members) {
    const k = m.name.split(' — ')[0]!;
    const cur = out.get(k);
    if (cur) cur.variants++;
    else out.set(k, { first: m, variants: 1 });
  }
  return [...out.values()];
}

/** Products that replace one another — approved groups drive the brand-switch suggestions. */
export function SubstitutesPage() {
  const { data } = useQuery({ queryKey: ['profit', 'substitutes'], queryFn: () => api<Groups>('/api/profit/substitutes') });
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Group | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['profit'] });

  const decide = async (ids: number[], status: Group['status']) => {
    setBusy(true);
    try {
      await api('/api/profit/substitutes/decide', { body: { ids, status } });
      refresh();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (g: Group) => {
    if (!(await confirm({ title: `למחוק את הקבוצה "${g.name}"?`, body: 'המוצרים עצמם לא נמחקים.', confirmLabel: 'מחיקה', danger: true }))) return;
    await api(`/api/profit/substitutes/${g.id}`, { method: 'DELETE' }).catch((e) => toast.error(e));
    refresh();
  };

  const proposed = data?.groups.filter((g) => g.status === 'proposed') ?? [];
  const approved = data?.groups.filter((g) => g.status === 'approved') ?? [];
  const rejected = data?.groups.filter((g) => g.status === 'rejected') ?? [];

  const section = (title: string, list: Group[], actions: (g: Group) => React.ReactNode, head?: React.ReactNode) =>
    list.length ? (
      <section className="panel">
        <div className="panel-head row wrap"><h2>{title} ({fmtNumber(list.length)})</h2><span className="spacer" />{head}</div>
        <div className="panel-body stack" style={{ gap: 12 }}>
          {list.map((g) => (
            <div key={g.id} className="stack" style={{ gap: 4, borderBottom: '1px solid var(--line)', paddingBottom: 10 }}>
              <div className="row wrap" style={{ gap: 8 }}>
                <span className="strong">{g.name}</span>
                {g.note ? <span className="small muted">· {g.note}</span> : null}
                <span className="spacer" />
                {actions(g)}
              </div>
              <div className="row wrap" style={{ gap: 6 }}>
                {byProduct(g.members).map(({ first: m, variants }) => (
                  <span key={m.productId} className="badge outline" title={`מחיר בסיס ${fmtMoney(m.basePrice)} · עלות ${fmtMoney(m.cost)}`}>
                    <Ltr>{(variants > 1 ? m.sku.replace(/^(\d+-\d+)-\d+$/, '$1') : m.sku)}</Ltr> {m.name.split(' — ')[0]}
                    {variants > 1 ? <span className="muted"> ({variants} וריאציות)</span> : null} {m.margin != null ? <MarginBadge margin={m.margin} level={m.marginLevel} /> : null}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    ) : null;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumbs"><Link to="/profit">רווחיות</Link></div>
          <h1>קבוצות תחליפים</h1>
          <div className="sub">
            מוצרים שלקוח יכול לקחת אחד במקום השני (נוזל כלים סנו / טאץ'). לקבוצות המאושרות המערכת מציעה ללקוחות החלפת מותג שלא מייקרת להם ומשאירה יותר רווח. האחוז ליד כל מוצר — הרווחיות במחיר הבסיס.
          </div>
        </div>
        <Button variant="primary" onClick={() => setEditing('new')}><IconPlus /> קבוצה חדשה</Button>
      </div>
      <div className="stack">
        {proposed.length ? <Callout tone="info">הוכנו {fmtNumber(proposed.length)} הצעות מהקטלוג. עוברים עליהן פעם אחת: מאשרים, דוחים, או עורכים (עריכה מאשרת).</Callout> : null}
        {section(
          'הצעות לאישור',
          proposed,
          (g) => (
            <>
              <Button size="sm" variant="primary" onClick={() => decide([g.id], 'approved')} disabled={busy}>אישור</Button>
              <Button size="sm" onClick={() => setEditing(g)}>עריכה</Button>
              <Button size="sm" variant="ghost" onClick={() => decide([g.id], 'rejected')} disabled={busy}>דחייה</Button>
            </>
          ),
          <Button size="sm" variant="primary" onClick={() => decide(proposed.map((g) => g.id), 'approved')} loading={busy}>אישור כל ההצעות</Button>,
        )}
        {section('מאושרות', approved, (g) => (
          <>
            <Button size="sm" onClick={() => setEditing(g)}>עריכה</Button>
            <Button size="sm" variant="ghost" icon onClick={() => remove(g)} aria-label="מחיקה"><IconTrash size={14} /></Button>
          </>
        ))}
        {section('נדחו', rejected, (g) => <Button size="sm" variant="ghost" onClick={() => decide([g.id], 'proposed')}>החזרה להצעות</Button>)}
        {data && !data.groups.length ? <div className="panel panel-body muted">אין עדיין קבוצות.</div> : null}
      </div>
      {editing ? <GroupDialog group={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={refresh} /> : null}
    </>
  );
}

function GroupDialog({ group, onClose, onSaved }: { group: Group | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(group?.name ?? '');
  const [members, setMembers] = useState<Array<{ productId: number; sku: string; name: string }>>(group?.members ?? []);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: name.trim(), productIds: members.map((m) => m.productId), note: group?.note ?? null };
      await api(group ? `/api/profit/substitutes/${group.id}` : '/api/profit/substitutes', { method: group ? 'PUT' : 'POST', body });
      toast.success('הקבוצה נשמרה ואושרה');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={group ? 'עריכת קבוצה' : 'קבוצת תחליפים חדשה'}
      footer={
        <>
          <Button variant="primary" onClick={save} loading={busy} disabled={!name.trim() || members.length < 2}>שמירה ואישור</Button>
          <Button onClick={onClose}>ביטול</Button>
        </>
      }
    >
      <div className="stack">
        <Field label="שם הקבוצה" required><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="למשל: נוזל כלים 750 מ״ל" /></Field>
        <div className="stack" style={{ gap: 4 }}>
          {members.map((m) => (
            <div key={m.productId} className="row" style={{ gap: 8 }}>
              <Ltr className="muted small">{m.sku}</Ltr>
              <span>{m.name}</span>
              <span className="spacer" />
              <Button size="sm" variant="ghost" icon onClick={() => setMembers(members.filter((x) => x.productId !== m.productId))} aria-label="הסרה"><IconTrash size={14} /></Button>
            </div>
          ))}
        </div>
        <ProductPicker placeholder="הוספת מוצר לקבוצה" onPick={(p) => !members.some((m) => m.productId === p.id) && setMembers([...members, { productId: p.id, sku: p.sku, name: p.name }])} />
        <p className="small muted" style={{ margin: 0 }}>מוצרים באותו שימוש ובגודל דומה — כך ההשוואה לפי מחיר לחבילה נכונה.</p>
      </div>
    </Dialog>
  );
}
